import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ListFilter, Plus, X, Search, ChevronRight, ChevronLeft, EyeOff, Check } from "lucide-react";
import Segment from "./Segment";
import EmptyState from "./EmptyState";
import { useJeMobil, omezenyPohyb } from "../lib/mobil";
import { useUrlStav } from "../lib/navigace";
import { Kbd, useZkratky } from "../lib/zkratky";
import { dekoduj, zakoduj, operator, prepniHodnotu, pocty, bezDiakritiky, sklon } from "../lib/filtr";

/**
 * Filtr z pilulek (Linear) pro seznamy — Klienti, Obchod, Nabídky, Servis.
 *
 * pole = [{ klic, nazev, rod, ikona, tvary?, hodnoty(polozky) → [{ v, label, tecka? }], hodnotyPolozky(p) → [v] }]
 * Filtry a pohled žijí v adrese (`f`, `pohled`), ať odkaz i Zpět fungují.
 * Pohled je rozsah stránky (Všechny / Moje / …), filtr zužuje v něm.
 */
export function useFiltr(pole) {
  const [fUrl, setFUrl] = useUrlStav("f", "");
  const filtry = useMemo(() => dekoduj(fUrl, pole), [fUrl, pole]);
  // funkční tvar čte aktuální adresu — dvě změny za sebou se nepřepíšou
  const setFiltry = (dalsi) => setFUrl((akt) => zakoduj(typeof dalsi === "function" ? dalsi(dekoduj(akt, pole)) : dalsi));
  return [filtry, setFiltry];
}

const popisHodnoty = (def, v, seznam) => (seznam.find((h) => h.v === v)?.label) ?? def.popis?.(v) ?? v;

function Tecka({ barva }) {
  return barva ? <span className="h-2 w-2 rounded-full shrink-0" style={{ background: barva }} /> : null;
}

/** Menu filtru: krok „Filtrovat podle…“ (pole) a krok hodnot se zaškrtávátky. */
function FiltrMenu({ pole, polozky, filtry, setFiltry, start, kotva, onClose }) {
  const mobil = useJeMobil();
  const [krok, setKrok] = useState(start.krok);
  const [klic, setKlic] = useState(start.pole || null);
  const [dotaz, setDotaz] = useState("");
  const [aktivni, setAktivni] = useState(0);
  const [poloha, setPoloha] = useState(null);
  const menu = useRef(null);
  const vstup = useRef(null);
  const def = pole.find((p) => p.klic === klic);

  const hodnotyPole = useMemo(() => Object.fromEntries(pole.map((p) => [p.klic, p.hodnoty(polozky)])), [pole, polozky]);
  const pocetHodnot = useMemo(() => (def ? pocty(polozky, def) : new Map()), [def, polozky]);

  // řádky aktuálního kroku: { typ: "nadpis" | "pole" | "hodnota", … }
  const radky = useMemo(() => {
    const q = bezDiakritiky(dotaz.trim());
    if (krok === "hodnoty" && def) {
      return hodnotyPole[def.klic].filter((h) => !q || bezDiakritiky(h.label).includes(q))
        .map((h) => ({ typ: "hodnota", pole: def.klic, h }));
    }
    const polePasuji = pole.filter((p) => !q || bezDiakritiky(p.nazev).includes(q)).map((p) => ({ typ: "pole", pole: p.klic }));
    if (!q) return polePasuji;
    const hodnoty = [];
    for (const p of pole) {
      for (const h of hodnotyPole[p.klic]) {
        if (hodnoty.length >= 8) break;
        if (bezDiakritiky(h.label).includes(q)) hodnoty.push({ typ: "hodnota", pole: p.klic, h, meta: p.nazev });
      }
    }
    return [
      ...(polePasuji.length ? [{ typ: "nadpis", text: "Pole" }, ...polePasuji] : []),
      ...(hodnoty.length ? [{ typ: "nadpis", text: "Hodnoty" }, ...hodnoty] : []),
    ];
  }, [krok, def, dotaz, pole, hodnotyPole]);
  const volitelne = radky.map((r, i) => (r.typ === "nadpis" ? -1 : i)).filter((i) => i >= 0);

  const zmer = () => {
    if (mobil || !kotva) return;
    const r = kotva.getBoundingClientRect();
    const sirka = 300;
    const left = Math.max(8, Math.min(r.left, window.innerWidth - sirka - 8));
    const dole = window.innerHeight - r.bottom - 14;
    const nahoru = dole < 280 && r.top > dole;
    setPoloha({ left, top: nahoru ? undefined : r.bottom + 6, bottom: nahoru ? window.innerHeight - r.top + 6 : undefined,
      max: Math.min(420, (nahoru ? r.top - 14 : dole)) });
  };
  useLayoutEffect(zmer, [mobil, kotva]); // eslint-disable-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    // na telefonu fokus na list, ne na vstup — ať nevyskočí klávesnice
    if (mobil) menu.current?.focus(); else vstup.current?.focus();
  }, [krok, mobil, !!poloha]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setAktivni(volitelne[0] ?? 0); }, [krok, dotaz]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const klik = (e) => { if (!menu.current?.contains(e.target) && !kotva?.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", klik);
    window.addEventListener("resize", zmer);
    return () => { document.removeEventListener("mousedown", klik); window.removeEventListener("resize", zmer); };
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const naPole = (k) => {
    if (start.zmenit && start.zmenit !== k) {
      // „Změnit pole na…“ — staré pole s hodnotami pryč, rovnou hodnoty nového
      setFiltry((fs) => fs.filter((f) => f.pole !== start.zmenit));
    }
    setKlic(k); setKrok("hodnoty"); setDotaz("");
  };
  const naHodnotu = (r, nechatOtevrene) => {
    const uz = filtry.find((f) => f.pole === r.pole)?.hodnoty.includes(r.h.v);
    if (nechatOtevrene || !uz) setFiltry((fs) => prepniHodnotu(fs, r.pole, r.h.v));
    if (!nechatOtevrene) onClose();
  };
  const vyber = (i, nechatOtevrene = false) => {
    const r = radky[i];
    if (!r) return;
    if (r.typ === "pole") naPole(r.pole);
    else if (r.typ === "hodnota") naHodnotu(r, nechatOtevrene);
  };

  const klavesy = (e) => {
    const pos = volitelne.indexOf(aktivni);
    if (e.key === "Escape") { e.preventDefault(); onClose(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setAktivni(volitelne[(pos + 1) % volitelne.length]); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setAktivni(volitelne[(pos - 1 + volitelne.length) % volitelne.length]); }
    else if ((e.key === "Home" || e.key === "End") && !dotaz) {
      e.preventDefault(); setAktivni(e.key === "Home" ? volitelne[0] : volitelne[volitelne.length - 1]);
    } else if (e.key === "Enter") { e.preventDefault(); vyber(aktivni, e.shiftKey); }
    else if (e.key === "Backspace" && !dotaz && krok === "hodnoty") { e.preventDefault(); setKrok("pole"); setKlic(null); }
  };

  useEffect(() => {
    menu.current?.querySelector(`[data-radek="${aktivni}"]`)?.scrollIntoView({ block: "nearest" });
  }, [aktivni]);

  const obsah = (
    <>
      <div className="flex items-center gap-2 px-3 h-[46px] border-b border-line-softer shrink-0">
        <Search size={15} className="text-muted shrink-0" />
        {krok === "hodnoty" && def && (
          <button type="button" onClick={() => { setKrok("pole"); setKlic(null); setDotaz(""); }} data-testid="filtr-zpet"
            className="tap md:min-h-0 shrink-0 inline-flex items-center gap-0.5 h-7 px-1.5 -ml-1 rounded-[6px] bg-inputbg t-meta font-700 text-sec hover:text-ink">
            <ChevronLeft size={13} /> {def.nazev}
          </button>
        )}
        <input ref={vstup} value={dotaz} onChange={(e) => setDotaz(e.target.value)} onKeyDown={klavesy}
          placeholder={krok === "hodnoty" && def ? `${def.nazev}…` : "Filtrovat podle…"} aria-label="Hledat v filtru"
          className="flex-1 min-w-0 bg-transparent outline-none text-[16px] md:text-[13px]" data-testid="filtr-hledat" />
      </div>
      <div className="overflow-y-auto min-h-0 p-1.5" role="listbox" aria-multiselectable={krok === "hodnoty"}>
        {radky.map((r, i) => {
          if (r.typ === "nadpis") return <div key={`n${i}`} className="px-2.5 pt-2 pb-1 label-up text-muted-2">{r.text}</div>;
          const p = pole.find((x) => x.klic === r.pole);
          const Ikona = p.ikona;
          if (r.typ === "pole") {
            return (
              <button key={`p${r.pole}`} type="button" data-radek={i} data-aktivni={i === aktivni} onMouseEnter={() => setAktivni(i)}
                onClick={() => vyber(i)} data-testid={`filtr-pole-${r.pole}`}
                className="vyber-polozka w-full min-h-[44px] md:min-h-[34px] px-2.5 rounded-[8px] flex items-center gap-2.5 text-left t-telo md:text-[13px] text-ink">
                {Ikona && <Ikona size={15} className="text-muted shrink-0" />}
                <span className="flex-1 min-w-0 truncate font-600">{p.nazev}</span>
                {start.zmenit === r.pole && <Check size={14} className="text-success" />}
                <ChevronRight size={14} className="text-muted-2 shrink-0" />
              </button>
            );
          }
          const zaskrtnuto = !!filtry.find((f) => f.pole === r.pole)?.hodnoty.includes(r.h.v);
          const n = r.meta ? null : pocetHodnot.get(r.h.v) || 0;
          return (
            <div key={`h${r.pole}${r.h.v}`} role="option" aria-selected={zaskrtnuto} data-radek={i} data-aktivni={i === aktivni}
              onMouseEnter={() => setAktivni(i)} onClick={() => vyber(i, mobil)} data-testid={`filtr-hodnota-${r.pole}-${r.h.v}`}
              className="vyber-polozka cursor-pointer w-full min-h-[48px] md:min-h-[34px] px-2.5 rounded-[8px] flex items-center gap-2.5 t-telo md:text-[13px] text-ink">
              <button type="button" aria-label={zaskrtnuto ? `Odebrat ${r.h.label}` : `Přidat ${r.h.label}`} tabIndex={-1}
                onClick={(e) => { e.stopPropagation(); vyber(i, true); }}
                className={`shrink-0 h-[22px] w-[22px] md:h-4 md:w-4 rounded-[5px] md:rounded-[4px] border flex items-center justify-center ${
                  zaskrtnuto ? "bg-ink border-ink text-white" : "bg-white border-line-strong"}`}>
                {zaskrtnuto && <Check size={12} strokeWidth={3} />}
              </button>
              <Tecka barva={r.h.tecka} />
              <span className="flex-1 min-w-0 truncate">{r.h.label}</span>
              {r.meta ? <span className="t-meta text-muted-2 shrink-0">{r.meta}</span>
                : <span className="amount t-meta text-muted-2 shrink-0">{n}</span>}
            </div>
          );
        })}
        {!radky.length && <div className="px-2.5 py-3 t-meta text-muted">Nic nenalezeno</div>}
      </div>
    </>
  );

  if (mobil) {
    return createPortal(
      <div className="fixed inset-0 z-[80] flex items-end" data-testid="filtr-menu">
        <div className="absolute inset-0 bg-black/30 zaclon-in" onClick={onClose} />
        <div ref={menu} tabIndex={-1} onKeyDown={klavesy} role="dialog" aria-modal="true" aria-label="Filtr"
          className="karta-panel relative bg-white w-full rounded-t-sheet shadow-panel max-h-[80vh] flex flex-col outline-none"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
          <div className="pt-2 pb-1 flex justify-center shrink-0"><span className="h-[5px] w-[38px] rounded-full bg-line-strong" /></div>
          <div className="flex items-center px-4 pb-1 shrink-0">
            <span className="t-sekce font-700 text-ink flex-1">{krok === "hodnoty" && def ? def.nazev : "Přidat filtr"}</span>
            <button type="button" onClick={onClose} aria-label="Zavřít" className="tap -mr-2 text-muted"><X size={20} /></button>
          </div>
          {obsah}
          {krok === "hodnoty" && (
            <div className="px-4 py-3 border-t border-line-softer shrink-0">
              <button type="button" onClick={onClose} className="btn-primary tap w-full justify-center" data-testid="filtr-hotovo">Hotovo</button>
            </div>
          )}
        </div>
      </div>, document.body);
  }
  if (!poloha) return null;
  return createPortal(
    <div ref={menu} onKeyDown={klavesy} data-testid="filtr-menu" role="dialog" aria-label="Filtr"
      className={`${omezenyPohyb() ? "" : "vyber-menu"} fixed z-[200] w-[300px] bg-white rounded-[14px] border border-line-softer shadow-panel flex flex-col`}
      style={{ left: poloha.left, top: poloha.top, bottom: poloha.bottom, maxHeight: poloha.max }}>
      {obsah}
    </div>, document.body);
}

/** Jedna pilulka: [pole][je / není][hodnota][×]. */
function Pilulka({ def, filtr, hodnoty, onPole, onOp, onHodnota, onOdebrat, testid }) {
  const prvni = filtr.hodnoty[0];
  const vic = filtr.hodnoty.length > 1;
  const text = vic
    ? `${filtr.hodnoty.length} ${def.tvary ? sklon(filtr.hodnoty.length, def.tvary) : "možnosti"}`
    : popisHodnoty(def, prvni, hodnoty);
  const tecka = !vic && hodnoty.find((h) => h.v === prvni)?.tecka;
  const op = operator(def.rod, filtr.neg, filtr.hodnoty.length);
  const cast = "tap md:min-h-0 md:min-w-0 h-11 md:h-7 px-2.5 md:px-2 inline-flex items-center gap-1.5 hover:bg-inputbg focus-visible:outline-none focus-visible:bg-inputbg";
  const Ikona = def.ikona;
  const klavesy = (e) => { if (e.key === "Backspace" || e.key === "Delete") { e.preventDefault(); onOdebrat(); } };
  return (
    <div role="group" aria-label={`Filtr: ${def.nazev} ${op} ${text}`} onKeyDown={klavesy} data-testid={testid}
      className="inline-flex items-stretch bg-white border border-line-strong rounded-[11px] md:rounded-[7px] overflow-hidden t-meta md:text-[12.5px] max-w-full">
      <button type="button" onClick={onPole} className={`${cast} font-700 text-ink`} data-cast="pole">
        {Ikona && <Ikona size={13} className="text-muted shrink-0" />}{def.nazev}
      </button>
      <button type="button" onClick={onOp} data-cast="op" aria-label={`Podmínka: ${op}. Přepnout na ${filtr.neg ? "je" : "není"}`}
        className={`${cast} shadow-[-1px_0_0_#e0deda] ${filtr.neg ? "text-danger" : "text-sec"}`}>{op}</button>
      <button type="button" onClick={onHodnota} data-cast="hodnota" className={`${cast} shadow-[-1px_0_0_#e0deda] font-600 text-ink min-w-0`}>
        <Tecka barva={tecka} /><span className="truncate max-w-[180px]">{text}</span>
      </button>
      <button type="button" onClick={onOdebrat} data-cast="x" aria-label={`Odebrat filtr ${def.nazev}`}
        className={`${cast} shadow-[-1px_0_0_#e0deda] text-muted hover:text-ink`}><X size={14} /></button>
    </div>
  );
}

/**
 * Lišta seznamu: pohledy, Filtr (F), pilulky, hledání (/).
 * vysledek / celkem = kolik po filtru zbylo z položek pohledu.
 */
export default function FiltrLista({ pole, polozky, filtry, setFiltry, pohledy = [], pohled, setPohled,
  vysledek, celkem, hledani, tvary = ["položka", "položky", "položek"], vlevo = null, vpravo = null, testid = "filtr-lista" }) {
  const mobil = useJeMobil();
  const [menu, setMenu] = useState(null);           // { krok, pole?, zmenit?, kotva }
  const tlacitko = useRef(null);
  const hledaniRef = useRef(null);
  const lista = useRef(null);
  const hodnotyPole = useMemo(() => Object.fromEntries(pole.map((p) => [p.klic, p.hodnoty(polozky)])), [pole, polozky]);

  const otevri = (stav, kotva = tlacitko.current) => setMenu({ ...stav, kotva });
  const zavri = () => {
    setMenu(null);
    // filtry bez hodnot po zavření pryč (adresa je stejně neuloží)
    setFiltry((fs) => fs.filter((f) => f.hodnoty.length));
  };
  useZkratky({
    f: () => otevri({ krok: "pole" }),
    "/": () => hledaniRef.current?.focus(),
  });

  const odeber = (i) => {
    setFiltry((fs) => fs.filter((_, j) => j !== i));
    // fokus na × další pilulky, pak předchozí, nakonec na tlačítko Filtr
    requestAnimationFrame(() => {
      const krize = lista.current?.querySelectorAll('[data-cast="x"]') || [];
      (krize[i] || krize[i - 1] || tlacitko.current)?.focus();
    });
  };
  const vymazat = () => setFiltry([]);
  const pohledText = (p) => (
    <>{p.nazev}{p.pocet != null && <span className="filtr-pocet">{p.pocet}</span>}</>
  );

  return (
    <div className="mb-4 flex flex-col gap-2.5" data-testid={testid} ref={lista}>
      <div className="flex flex-wrap items-center gap-2">
        {hledani && (
          <label className="order-first md:order-last w-full md:w-[260px] md:ml-auto flex items-center gap-2 field h-11 md:h-9">
            <Search size={15} className="text-muted shrink-0" />
            <input ref={hledaniRef} className="bg-transparent outline-none w-full min-w-0 text-[16px] md:text-[13px]"
              placeholder={hledani.placeholder} value={hledani.q} onChange={(e) => hledani.setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Escape") e.currentTarget.blur(); }} data-testid={hledani.testid} />
            <Kbd>/</Kbd>
          </label>
        )}
        {vlevo}
        {pohledy.length > 0 && (
          <Segment className="filtr-pas max-w-full" data-testid={`${testid}-pohledy`}>
            {pohledy.map((p) => (
              <button key={p.klic} type="button" data-on={pohled === p.klic} onClick={() => setPohled(p.klic)} title={p.popis}
                className={`filtr-seg gap-1.5 transition-colors ${pohled === p.klic ? "text-white" : "text-sec hover:text-ink"}`}
                data-testid={`pohled-${p.klic}`}>{pohledText(p)}</button>
            ))}
          </Segment>
        )}
        {/* na telefonu s filtrem přidává další filtr „+“ v řádku pilulek */}
        {!(mobil && filtry.length > 0) && (
          <button ref={tlacitko} type="button" onClick={(e) => (menu ? zavri() : otevri({ krok: "pole" }, e.currentTarget))}
            aria-haspopup="dialog" aria-expanded={!!menu} data-testid={`${testid}-tlacitko`}
            className="filtr bg-white text-ink border-line-strong hover:bg-inputbg">
            <ListFilter size={15} /> {mobil ? "Přidat filtr" : "Filtr"} <Kbd>F</Kbd>
          </button>
        )}
        {vpravo && <span className="order-last flex items-center gap-2">{vpravo}</span>}
      </div>

      {(filtry.length > 0 || !mobil) && (
        <div className="flex flex-wrap items-center gap-1.5 min-h-[28px]" data-testid={`${testid}-pilulky`}>
          {filtry.map((f, i) => {
            const def = pole.find((p) => p.klic === f.pole);
            return (
              <Pilulka key={f.pole} def={def} filtr={f} hodnoty={hodnotyPole[f.pole] || []} testid={`pilulka-${f.pole}`}
                onPole={(e) => otevri({ krok: "pole", zmenit: f.pole }, e.currentTarget)}
                onOp={() => setFiltry((fs) => fs.map((x, j) => (j === i ? { ...x, neg: !x.neg } : x)))}
                onHodnota={(e) => otevri({ krok: "hodnoty", pole: f.pole }, e.currentTarget)}
                onOdebrat={() => odeber(i)} />
            );
          })}
          {filtry.length > 0 ? (
            <button type="button" onClick={(e) => otevri({ krok: "pole" }, e.currentTarget)} aria-label="Přidat filtr"
              className="tap md:min-h-0 h-[46px] w-[46px] md:h-7 md:w-7 rounded-[11px] md:rounded-[7px] border border-dashed border-line-strong text-muted hover:text-ink inline-flex items-center justify-center"
              data-testid={`${testid}-plus`}>
              <Plus size={15} />
            </button>
          ) : (
            <span className="hidden md:inline-flex items-center gap-1 t-meta text-muted">Bez filtru · stiskni <Kbd>F</Kbd> a piš</span>
          )}
          <span className="ml-auto flex items-center gap-3 t-meta text-muted">
            {filtry.length > 0 && (
              <>
                <span data-testid={`${testid}-vysledek`}><b className="amount text-ink">{vysledek}</b> z <span className="amount">{celkem}</span></span>
                <button type="button" onClick={vymazat} className="tap md:min-h-0 font-700 text-sec hover:text-ink" data-testid={`${testid}-vymazat`}>Vymazat</button>
              </>
            )}
            {!filtry.length && !mobil && <span className="amount">{celkem} {sklon(celkem, tvary)}</span>}
          </span>
        </div>
      )}

      {menu && (
        <FiltrMenu pole={pole} polozky={polozky} filtry={filtry} setFiltry={setFiltry} start={menu}
          kotva={menu.kotva} onClose={zavri} />
      )}
    </div>
  );
}

/** Čárkovaný řádek pod výsledky: kolik položek filtr schoval. */
export function SkrytoFiltrem({ skryto, tvary, onZobrazit }) {
  if (!skryto) return null;
  return (
    <div className="mx-3 md:mx-4 my-3 rounded-[10px] border border-dashed border-line-strong px-3.5 py-2.5 flex flex-wrap items-center gap-x-2 gap-y-1 t-meta text-muted"
      data-testid="skryto-filtrem">
      <EyeOff size={14} className="shrink-0" />
      <span><b className="amount text-ink">{skryto}</b> {sklon(skryto, tvary)} skrytých filtrem</span>
      <span aria-hidden="true">·</span>
      <button type="button" onClick={onZobrazit} className="tap md:min-h-0 font-700 text-sec hover:text-ink">Zobrazit vše</button>
    </div>
  );
}

/** Nic neodpovídá filtru. */
export function PrazdnyFiltr({ nazev = "Žádná položka", onVymazat }) {
  return (
    <EmptyState icon="ListFilter" title={`${nazev} neodpovídá filtru`} description="Uprav pilulku nebo filtr smaž."
      action={<button type="button" onClick={onVymazat} className="btn-ghost tap md:min-h-0 h-10" data-testid="filtr-prazdny-vymazat">Vymazat filtry</button>} />
  );
}
